import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { ApiConfiguration } from 'src/app/backend-client/api-configuration';
import { SimpleResponse } from 'src/app/backend-client/models/SharedKernel/Models/simple-response';
import { MxIdentityService } from './mx.identity.service';
import { SzShellService } from './sz-shell.service';

/** Az ügyfélportál értesítéseinek típusai (szerver: PortalNotificationKind). */
export enum PortalNotificationKind {
  NewPost = 10,
  DocumentUploaded = 20,
  OfferPublished = 25,
  Substitution = 30,
  Claim = 40,
  SziromPont = 50,
  /** Hibajelzés: állapotváltás a patikának (/issue-reports?id=…), új bejelentés az adminnak (/issue-reports). */
  IssueReport = 60,
}

export interface PortalNotificationDto {
  id: number;
  kind: PortalNotificationKind;
  kindLabel: string;
  title: string;
  body: string;
  link?: string | null;
  createdAt: string;
  readAt?: string | null;
}

export interface PortalNotificationCollectionResponse {
  items: PortalNotificationDto[];
  totalCount: number;
  unreadCount: number;
}

export interface NotificationPreferenceDto {
  kind: PortalNotificationKind;
  label: string;
  description: string;
  inPortal: boolean;
  byEmail: boolean;
}

/** Ezzel a fejléccel jelölt kérésekre nem jelenik meg a globális betöltő (háttér-lekérdezés). */
export const SZ_SILENT_HEADER = 'X-Sz-Silent';

/**
 * Ügyfélportál-értesítések (harang): percenkénti háttér-frissítés bejelentkezett felhasználónál,
 * olvasottnak jelölés, és a Profil oldal értesítési beállításai.
 */
@Injectable({ providedIn: 'root' })
export class SzNotificationService implements OnDestroy {
  private static readonly POLL_MS = 60_000;

  readonly items$ = new BehaviorSubject<PortalNotificationDto[]>([]);
  readonly unread$ = new BehaviorSubject<number>(0);

  private timer: Subscription | null = null;
  private lastAccountId: number | null = null;

  constructor(
    private http: HttpClient,
    private api: ApiConfiguration,
    private identity: MxIdentityService,
    private shell: SzShellService
  ) {
    this.identity.account.subscribe((a) => {
      const id = a?.id ?? null;
      if (id && id !== this.lastAccountId) {
        this.lastAccountId = id;
        this.start();
      } else if (!id) {
        this.lastAccountId = null;
        this.stop();
      }
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Azonnali frissítés (pl. panel nyitásakor). */
  refresh(): Observable<PortalNotificationCollectionResponse | null> {
    if (!this.identity.accountValue) return of(null);
    return this.http
      .post<PortalNotificationCollectionResponse>(`${this.api.rootUrl}/api/app/notifications/list`, { onlyUnread: false, take: 50 }, { headers: this.silent() })
      .pipe(
        tap((r) => this.apply(r)),
        catchError(() => of(null))
      );
  }

  markRead(ids: number[]): Observable<SimpleResponse | null> {
    if (!ids.length) return of(null);
    return this.http.post<SimpleResponse>(`${this.api.rootUrl}/api/app/notifications/mark-read`, { ids, all: false }, { headers: this.silent() }).pipe(
      tap(() => {
        const now = new Date().toISOString();
        const items = this.items$.value.map((n) => (ids.includes(n.id) && !n.readAt ? { ...n, readAt: now } : n));
        this.items$.next(items);
        this.setUnread(items.filter((n) => !n.readAt).length);
      }),
      catchError(() => of(null))
    );
  }

  markAllRead(): Observable<SimpleResponse | null> {
    return this.http.post<SimpleResponse>(`${this.api.rootUrl}/api/app/notifications/mark-read`, { ids: [], all: true }, { headers: this.silent() }).pipe(
      tap(() => {
        const now = new Date().toISOString();
        this.items$.next(this.items$.value.map((n) => (n.readAt ? n : { ...n, readAt: now })));
        this.setUnread(0);
      }),
      catchError(() => of(null))
    );
  }

  getPreferences(): Observable<{ items: NotificationPreferenceDto[] }> {
    return this.http.get<{ items: NotificationPreferenceDto[] }>(`${this.api.rootUrl}/api/app/notifications/preferences`);
  }

  savePreferences(items: { kind: PortalNotificationKind; inPortal: boolean; byEmail: boolean }[]): Observable<SimpleResponse> {
    return this.http.post<SimpleResponse>(`${this.api.rootUrl}/api/app/notifications/preferences`, { items });
  }

  /** Material Symbol az értesítés típusához. */
  static icon(kind: PortalNotificationKind): string {
    switch (kind) {
      case PortalNotificationKind.NewPost:
        return 'newspaper';
      case PortalNotificationKind.DocumentUploaded:
        return 'description';
      case PortalNotificationKind.OfferPublished:
        return 'percent';
      case PortalNotificationKind.Substitution:
        return 'swap_horiz';
      case PortalNotificationKind.Claim:
        return 'report';
      case PortalNotificationKind.SziromPont:
        return 'stars';
      case PortalNotificationKind.IssueReport:
        return 'bug_report';
      default:
        return 'notifications';
    }
  }

  private start() {
    this.stop();
    this.refresh().subscribe();
    this.timer = interval(SzNotificationService.POLL_MS)
      .pipe(
        filter(() => !!this.identity.accountValue && typeof document !== 'undefined' && document.visibilityState !== 'hidden'),
        switchMap(() => this.refresh())
      )
      .subscribe();
  }

  private stop() {
    this.timer?.unsubscribe();
    this.timer = null;
    this.items$.next([]);
    this.setUnread(0);
  }

  private apply(r: PortalNotificationCollectionResponse | null) {
    if (!r) return;
    this.items$.next(r.items ?? []);
    this.setUnread(r.unreadCount ?? 0);
  }

  private setUnread(n: number) {
    this.unread$.next(n);
    this.shell.setBadge(n);
  }

  private silent(): HttpHeaders {
    return new HttpHeaders({ [SZ_SILENT_HEADER]: '1' });
  }
}
